import React, { CSSProperties } from "react";
import Dropzone from "react-dropzone";
import UsersAPI from "../../network/UsersAPI";
import { sweetalert } from "../../App";
import Overlay from "../Overlay";

const GREY = '#CCC';
const GREY_DIM = '#686868';

const dropzoneStyles = {
    zone: {
        alignItems: 'center',
        border: `2px dashed ${GREY}`,
        borderRadius: 20,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 150,
        justifyContent: 'center',
        padding: 20,
        marginBottom: 10,
        cursor: 'pointer',
        transition: 'border-color 0.2s ease'
    } as CSSProperties,
    zoneHover: {
        borderColor: GREY_DIM,
    } as CSSProperties,
    file: {
        background: 'linear-gradient(to bottom, #EEE, #DDD)',
        borderRadius: 20,
        display: 'flex',
        height: 120,
        width: 120,
        position: 'relative',
        zIndex: 10,
        flexDirection: 'column',
        justifyContent: 'center',
    } as CSSProperties,
    info: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: 10,
        paddingRight: 10,
    } as CSSProperties,
    size: {
        backgroundColor: 'rgba(255, 255, 255, 0.4)',
        borderRadius: 3,
        marginBottom: '0.5em',
        justifyContent: 'center',
        display: 'flex',
    } as CSSProperties,
    name: {
        backgroundColor: 'rgba(255, 255, 255, 0.4)',
        borderRadius: 3,
        fontSize: 12,
        marginBottom: '0.5em',
        textAlign: 'center',
        wordBreak: 'break-all'
    } as CSSProperties,
    remove: {
        height: 23,
        position: 'absolute',
        right: 6,
        top: 6,
        width: 23,
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        color: '#A01919',
        fontWeight: 'bold',
        fontSize: 16,
        lineHeight: '23px',
        textAlign: 'center',
        borderRadius: '50%'
    } as CSSProperties,
};

function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

interface IDVerificationUploadProps {
    userUUID: string;
    onUploadComplete: () => void;
}

interface IDVerificationUploadState {
    idFront: File | null;
    frontHover: boolean;
    showLoading: boolean;
}

const ACCEPTED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.heic', '.heif'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export default class IDVerificationUpload extends React.Component<IDVerificationUploadProps, IDVerificationUploadState> {
    constructor(props: IDVerificationUploadProps) {
        super(props);
        this.state = {
            idFront: null,
            frontHover: false,
            showLoading: false,
        };
    }

    async handleSubmit() {
        const { idFront } = this.state;
        if (!idFront) {
            sweetalert.fire({ icon: "warning", text: "Please upload a photo of your school ID." });
            return;
        }

        this.setState({ showLoading: true });
        try {
            const formData = new FormData();
            formData.append("idFront", idFront);
            formData.append("userUUID", this.props.userUUID);

            const response = await UsersAPI.uploadIDVerification(formData);
            if (response && response.success) {
                sweetalert.fire({ icon: "success", title: "ID Submitted", text: "Your school ID has been submitted for verification. You will receive an email once your account has been approved." });
                this.props.onUploadComplete();
            } else {
                sweetalert.fire({ icon: "error", text: response?.reason || "Unable to upload ID. Please try again later." });
            }
        } catch (error) {
            sweetalert.fire({ icon: "error", text: "An error occurred while uploading your school ID. Please try again later." });
        } finally {
            this.setState({ showLoading: false });
        }
    }

    handleDrop(acceptedFiles: File[], rejectedFiles: any[]) {
        this.setState({ frontHover: false });

        if (rejectedFiles && rejectedFiles.length > 0) {
            sweetalert.fire({ icon: 'error', title: '', text: 'File must be an image (JPG, PNG, HEIC) and under 10MB.' });
            return;
        }
        if (acceptedFiles && acceptedFiles.length > 0) {
            this.setState({ idFront: acceptedFiles[0] });
        }
    }

    renderDropzone() {
        const file = this.state.idFront;

        return (
            <div style={{ minWidth: 240 }}>
                <Dropzone
                    onDrop={(acceptedFiles, rejectedFiles) => this.handleDrop(acceptedFiles, rejectedFiles)}
                    onDragEnter={() => this.setState({ frontHover: true })}
                    onDragLeave={() => this.setState({ frontHover: false })}
                    accept={ACCEPTED_EXTENSIONS}
                    maxSize={MAX_FILE_SIZE}
                    multiple={false}
                >
                    {({ getRootProps, getInputProps }) => (
                        <div
                            {...getRootProps()}
                            style={Object.assign(
                                {},
                                dropzoneStyles.zone,
                                this.state.frontHover && dropzoneStyles.zoneHover
                            )}
                        >
                            <input {...getInputProps()} />
                            {file ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                                    <div style={dropzoneStyles.file}>
                                        <div style={dropzoneStyles.info}>
                                            <span style={dropzoneStyles.size}>{formatFileSize(file.size)}</span>
                                            <span style={dropzoneStyles.name}>{file.name}</span>
                                        </div>
                                        <button
                                            style={dropzoneStyles.remove}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                this.setState({ idFront: null });
                                            }}
                                            title="Remove file"
                                        >
                                            &times;
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <span style={{ color: '#686868' }}>Drop file here or click to upload</span>
                            )}
                        </div>
                    )}
                </Dropzone>
            </div>
        );
    }

    render() {
        const { idFront } = this.state;
        const canSubmit = !!idFront;

        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "80vh" }}>
                <Overlay show_loading={this.state.showLoading} />
                <div
                    style={{
                        width: "100%",
                        maxWidth: "650px",
                        padding: "2rem 2.5rem",
                        borderRadius: "5px",
                        boxShadow: "0 0 20px rgba(0, 0, 0, 0.15)",
                        backgroundColor: "#fff",
                        textAlign: "center",
                        fontFamily: "inherit",
                    }}
                >
                    {/* Icon matching the Data Agreement style */}
                    <div
                        style={{
                            width: "80px",
                            height: "80px",
                            borderRadius: "50%",
                            border: "4px solid #3fc3ee",
                            margin: "0 auto 1.25rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#3fc3ee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="5" width="20" height="14" rx="2" />
                            <circle cx="8" cy="12" r="2" />
                            <path d="M14 10h4" />
                            <path d="M14 14h4" />
                        </svg>
                    </div>

                    <h2 style={{ fontSize: "1.35rem", fontWeight: 600, marginBottom: "0.75rem", color: "#595959" }}>
                        Identity Verification Required
                    </h2>
                    <p style={{ fontSize: "0.95rem", color: "#545454", marginBottom: "1.5rem", lineHeight: 1.5 }}>
                        To complete your account setup, please upload a photo of your school ID.
                        Accepted formats are JPEG, PNG, HEIC, or HEIF (max 10MB).
                    </p>

                    <div style={{ marginBottom: "1.5rem" }}>
                        {this.renderDropzone()}
                    </div>

                    <button
                        onClick={() => this.handleSubmit()}
                        disabled={!canSubmit}
                        style={{
                            marginTop: "0.5rem",
                            padding: "0.625em 1.5em",
                            fontSize: "1rem",
                            fontWeight: 500,
                            border: "none",
                            borderRadius: "5px",
                            backgroundColor: canSubmit ? "#21212E" : "#ccc",
                            color: "#fff",
                            cursor: canSubmit ? "pointer" : "default",
                            minWidth: "160px",
                        }}
                    >
                        Submit for Verification
                    </button>
                </div>
            </div>
        );
    }
}
